import {useEffect} from 'react';
import {Breadcrumb, ProgressIndicator} from '@/components/atoms';
import {PageHeader, SaveCancelFooter} from '@/components/molecules';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {
  isVideoActivityType,
  isFreeKickActivityType,
  isWheelOfFortuneActivityType,
  isBackgammonActivityType
} from '@/types/Activity';
import {getModal} from '@/utils/CommonModal';
import useActivityDisplay from '@/hooks/useActivityDisplay';
import useFreeKickGameDisplay from '@/hooks/useFreeKickGameDisplay';
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook';
import VideoDisplayForm from '@/containers/Activity/VideoDisplayForm/VideoDisplayForm';
import FreeKickGameDisplayForm from '@/containers/Activity/FreeKickGameDisplay/FreeKickGameDisplayForm';
import WheelOfFortuneDisplayForm from '@/containers/Activity/WheelOfFortuneDisplayForm/WheelOfFortuneDisplayForm';
import BackgammonGameDisplayForm from '@/containers/Activity/BackgammonGameDisplay/BackgammonGameDisplayForm';
import {isActivitySaveDraftEnabled} from '@/helpers/activityUtils';

/**
 * Video/Game activity step 2 (Display). Setup is on Add Activity; Review follows.
 */
const ActivityDisplay = () => {
  const navigate = useNavigate();
  const {activityType, status} =
    useSelector((state) => state.activity.formData) || {};
  const isVideoFlow = isVideoActivityType(activityType);
  const isFreeKickGameFlow = isFreeKickActivityType(activityType);
  const isWheelOfFortuneFlow = isWheelOfFortuneActivityType(activityType);
  const isBackgammonFlow = isBackgammonActivityType(activityType);
  const isDisplayFlow =
    isVideoFlow ||
    isFreeKickGameFlow ||
    isWheelOfFortuneFlow ||
    isBackgammonFlow;

  // Video/WheelOfFortune display hook
  const activityDisplayHook = useActivityDisplay();

  // FreeKick display hook
  const freeKickDisplayHook = useFreeKickGameDisplay();

  // Select the appropriate hook based on activity type
  const activeHook =
    isFreeKickGameFlow || isBackgammonFlow
      ? freeKickDisplayHook
      : activityDisplayHook;

  const {handleDraft: handleReviewDraft} = useActivityReview();
  const handleDraft = (event) => {
    event?.stopPropagation();
    handleReviewDraft();
  };

  const handleNext = () => {
    if (isWheelOfFortuneFlow || isFreeKickGameFlow || isBackgammonFlow) {
      navigate(ROUTES.ACTIVITY_ATTRIBUTES);
    } else {
      navigate(ROUTES.ACTIVITY_REVIEW);
    }
  };

  useEffect(() => {
    if (
      activityType &&
      !isVideoActivityType(activityType) &&
      !isFreeKickActivityType(activityType) &&
      !isWheelOfFortuneActivityType(activityType) &&
      !isBackgammonActivityType(activityType)
    ) {
      navigate(ROUTES.ACTIVITY_ADD, {replace: true});
    }
  }, [activityType, navigate]);

  if (!isDisplayFlow) {
    return null;
  }

  const renderDisplayForm = () => {
    if (isVideoFlow) {
      return (
        <VideoDisplayForm
          languages={activityDisplayHook.languages}
          activeTabIndex={activityDisplayHook.activeTabIndex}
          setActiveTabIndex={activityDisplayHook.setActiveTabIndex}
          descActiveTabIndex={activityDisplayHook.descActiveTabIndex}
          setDescActiveTabIndex={activityDisplayHook.setDescActiveTabIndex}
          currentLangData={activityDisplayHook.currentLangData}
          descCurrentLangData={activityDisplayHook.descCurrentLangData}
          updateField={activityDisplayHook.updateField}
          updateDescField={activityDisplayHook.updateDescField}
          displayOrder={activityDisplayHook.displayOrder}
          setDisplayOrder={activityDisplayHook.setDisplayOrder}
          handleImageUpload={activityDisplayHook.handleImageUpload}
          handleImageDelete={activityDisplayHook.handleImageDelete}
          handleImageFileError={activityDisplayHook.handleImageFileError}
          handleVideoUpload={activityDisplayHook.handleVideoUpload}
          handleVideoDelete={activityDisplayHook.handleVideoDelete}
          handleVideoFileError={activityDisplayHook.handleVideoFileError}
          showGuidelines={activityDisplayHook.showGuidelines}
          setShowGuidelines={activityDisplayHook.setShowGuidelines}
          imageUploadProgress={activityDisplayHook.imageUploadProgress}
          videoUploadProgress={activityDisplayHook.videoUploadProgress}
          imageValidationError={activityDisplayHook.imageValidationError}
          videoValidationError={activityDisplayHook.videoValidationError}
        />
      );
    }

    if (isBackgammonFlow) {
      return (
        <BackgammonGameDisplayForm
          languages={freeKickDisplayHook.languages}
          activeTabIndex={freeKickDisplayHook.activeTabIndex}
          setActiveTabIndex={freeKickDisplayHook.setActiveTabIndex}
          currentLangData={freeKickDisplayHook.currentLangData}
          updateLangField={freeKickDisplayHook.updateLangField}
          gameData={freeKickDisplayHook.gameData}
          handleGameImageUpload={freeKickDisplayHook.handleGameImageUpload}
          handleGameImageDelete={freeKickDisplayHook.handleGameImageDelete}
          handleLogoImageUpload={freeKickDisplayHook.handleLogoImageUpload}
          handleLogoImageDelete={freeKickDisplayHook.handleLogoImageDelete}
          handleBackgroundMenuUpload={
            freeKickDisplayHook.handleBackgroundMenuUpload
          }
          handleBackgroundMenuDelete={
            freeKickDisplayHook.handleBackgroundMenuDelete
          }
          handleBackgroundImageUpload={
            freeKickDisplayHook.handleBackgroundImageUpload
          }
          handleBackgroundImageDelete={
            freeKickDisplayHook.handleBackgroundImageDelete
          }
          handleBackgroundVSUpload={
            freeKickDisplayHook.handleBackgroundVSUpload
          }
          handleBackgroundVSDelete={
            freeKickDisplayHook.handleBackgroundVSDelete
          }
          handleBackgroundWaitingUpload={
            freeKickDisplayHook.handleBackgroundWaitingUpload
          }
          handleBackgroundWaitingDelete={
            freeKickDisplayHook.handleBackgroundWaitingDelete
          }
          handleBackgroundExitUpload={
            freeKickDisplayHook.handleBackgroundExitUpload
          }
          handleBackgroundExitDelete={
            freeKickDisplayHook.handleBackgroundExitDelete
          }
          gameImageProgress={freeKickDisplayHook.gameImageProgress}
          logoImageProgress={freeKickDisplayHook.logoImageProgress}
          backgroundMenuProgress={freeKickDisplayHook.backgroundMenuProgress}
          backgroundImageProgress={freeKickDisplayHook.backgroundImageProgress}
          backgroundVSProgress={freeKickDisplayHook.backgroundVSProgress}
          backgroundWaitingProgress={
            freeKickDisplayHook.backgroundWaitingProgress
          }
          backgroundExitProgress={freeKickDisplayHook.backgroundExitProgress}
          gameImageValidationError={
            freeKickDisplayHook.gameImageValidationError
          }
          logoImageValidationError={
            freeKickDisplayHook.logoImageValidationError
          }
          backgroundMenuValidationError={
            freeKickDisplayHook.backgroundMenuValidationError
          }
          backgroundImageValidationError={
            freeKickDisplayHook.backgroundImageValidationError
          }
          backgroundVSValidationError={
            freeKickDisplayHook.backgroundVSValidationError
          }
          backgroundWaitingValidationError={
            freeKickDisplayHook.backgroundWaitingValidationError
          }
          backgroundExitValidationError={
            freeKickDisplayHook.backgroundExitValidationError
          }
          handleGameImageFileError={
            freeKickDisplayHook.handleGameImageFileError
          }
          handleLogoImageFileError={
            freeKickDisplayHook.handleLogoImageFileError
          }
          handleBackgroundMenuFileError={
            freeKickDisplayHook.handleBackgroundMenuFileError
          }
          handleBackgroundImageFileError={
            freeKickDisplayHook.handleBackgroundImageFileError
          }
          handleBackgroundVSFileError={
            freeKickDisplayHook.handleBackgroundVSFileError
          }
          handleBackgroundWaitingFileError={
            freeKickDisplayHook.handleBackgroundWaitingFileError
          }
          handleBackgroundExitFileError={
            freeKickDisplayHook.handleBackgroundExitFileError
          }
          showGuidelines={freeKickDisplayHook.showGuidelines}
          setShowGuidelines={freeKickDisplayHook.setShowGuidelines}
        />
      );
    }

    if (isFreeKickGameFlow) {
      return (
        <FreeKickGameDisplayForm
          languages={freeKickDisplayHook.languages}
          activeTabIndex={freeKickDisplayHook.activeTabIndex}
          setActiveTabIndex={freeKickDisplayHook.setActiveTabIndex}
          tutorialTabIndex={freeKickDisplayHook.tutorialTabIndex}
          setTutorialTabIndex={freeKickDisplayHook.setTutorialTabIndex}
          currentLangData={freeKickDisplayHook.currentLangData}
          tutorialLangData={freeKickDisplayHook.tutorialLangData}
          updateLangField={freeKickDisplayHook.updateLangField}
          updateTutorialField={freeKickDisplayHook.updateTutorialField}
          gameData={freeKickDisplayHook.gameData}
          updateGameField={freeKickDisplayHook.updateGameField}
          handleGameImageUpload={freeKickDisplayHook.handleGameImageUpload}
          handleGameImageDelete={freeKickDisplayHook.handleGameImageDelete}
          handleLogoImageUpload={freeKickDisplayHook.handleLogoImageUpload}
          handleLogoImageDelete={freeKickDisplayHook.handleLogoImageDelete}
          handleFirstTeamImageUpload={
            freeKickDisplayHook.handleFirstTeamImageUpload
          }
          handleFirstTeamImageDelete={
            freeKickDisplayHook.handleFirstTeamImageDelete
          }
          handleSecondTeamImageUpload={
            freeKickDisplayHook.handleSecondTeamImageUpload
          }
          handleSecondTeamImageDelete={
            freeKickDisplayHook.handleSecondTeamImageDelete
          }
          handleBackgroundMenuUpload={
            freeKickDisplayHook.handleBackgroundMenuUpload
          }
          handleBackgroundMenuDelete={
            freeKickDisplayHook.handleBackgroundMenuDelete
          }
          handleBackgroundSelectTeamUpload={
            freeKickDisplayHook.handleBackgroundSelectTeamUpload
          }
          handleBackgroundSelectTeamDelete={
            freeKickDisplayHook.handleBackgroundSelectTeamDelete
          }
          handleBackgroundImageUpload={
            freeKickDisplayHook.handleBackgroundImageUpload
          }
          handleBackgroundImageDelete={
            freeKickDisplayHook.handleBackgroundImageDelete
          }
          handleBackgroundNextLevelUpload={
            freeKickDisplayHook.handleBackgroundNextLevelUpload
          }
          handleBackgroundNextLevelDelete={
            freeKickDisplayHook.handleBackgroundNextLevelDelete
          }
          handleMissImageUpload={freeKickDisplayHook.handleMissImageUpload}
          handleMissImageDelete={freeKickDisplayHook.handleMissImageDelete}
          handleOutImageUpload={freeKickDisplayHook.handleOutImageUpload}
          handleOutImageDelete={freeKickDisplayHook.handleOutImageDelete}
          handleGameOverImageUpload={
            freeKickDisplayHook.handleGameOverImageUpload
          }
          handleGameOverImageDelete={
            freeKickDisplayHook.handleGameOverImageDelete
          }
          handleGoalImageUpload={freeKickDisplayHook.handleGoalImageUpload}
          handleGoalImageDelete={freeKickDisplayHook.handleGoalImageDelete}
          handleHighBarImageUpload={
            freeKickDisplayHook.handleHighBarImageUpload
          }
          handleHighBarImageDelete={
            freeKickDisplayHook.handleHighBarImageDelete
          }
          handleRightBarImageUpload={
            freeKickDisplayHook.handleRightBarImageUpload
          }
          handleRightBarImageDelete={
            freeKickDisplayHook.handleRightBarImageDelete
          }
          gameImageProgress={freeKickDisplayHook.gameImageProgress}
          logoImageProgress={freeKickDisplayHook.logoImageProgress}
          firstTeamImageProgress={freeKickDisplayHook.firstTeamImageProgress}
          secondTeamImageProgress={freeKickDisplayHook.secondTeamImageProgress}
          backgroundMenuProgress={freeKickDisplayHook.backgroundMenuProgress}
          backgroundSelectTeamProgress={
            freeKickDisplayHook.backgroundSelectTeamProgress
          }
          backgroundImageProgress={freeKickDisplayHook.backgroundImageProgress}
          backgroundNextLevelProgress={
            freeKickDisplayHook.backgroundNextLevelProgress
          }
          missImageProgress={freeKickDisplayHook.missImageProgress}
          outImageProgress={freeKickDisplayHook.outImageProgress}
          gameOverImageProgress={freeKickDisplayHook.gameOverImageProgress}
          goalImageProgress={freeKickDisplayHook.goalImageProgress}
          highBarImageProgress={freeKickDisplayHook.highBarImageProgress}
          rightBarImageProgress={freeKickDisplayHook.rightBarImageProgress}
          gameImageValidationError={
            freeKickDisplayHook.gameImageValidationError
          }
          logoImageValidationError={
            freeKickDisplayHook.logoImageValidationError
          }
          teamImageValidationError={
            freeKickDisplayHook.teamImageValidationError
          }
          secondTeamImageValidationError={
            freeKickDisplayHook.secondTeamImageValidationError
          }
          backgroundMenuValidationError={
            freeKickDisplayHook.backgroundMenuValidationError
          }
          backgroundSelectTeamValidationError={
            freeKickDisplayHook.backgroundSelectTeamValidationError
          }
          backgroundImageValidationError={
            freeKickDisplayHook.backgroundImageValidationError
          }
          backgroundNextLevelValidationError={
            freeKickDisplayHook.backgroundNextLevelValidationError
          }
          missImageValidationError={
            freeKickDisplayHook.missImageValidationError
          }
          outImageValidationError={freeKickDisplayHook.outImageValidationError}
          gameOverImageValidationError={
            freeKickDisplayHook.gameOverImageValidationError
          }
          goalImageValidationError={
            freeKickDisplayHook.goalImageValidationError
          }
          highBarImageValidationError={
            freeKickDisplayHook.highBarImageValidationError
          }
          rightBarImageValidationError={
            freeKickDisplayHook.rightBarImageValidationError
          }
          handleGameImageFileError={
            freeKickDisplayHook.handleGameImageFileError
          }
          handleLogoImageFileError={
            freeKickDisplayHook.handleLogoImageFileError
          }
          handleFirstTeamImageFileError={
            freeKickDisplayHook.handleFirstTeamImageFileError
          }
          handleSecondTeamImageFileError={
            freeKickDisplayHook.handleSecondTeamImageFileError
          }
          handleBackgroundMenuFileError={
            freeKickDisplayHook.handleBackgroundMenuFileError
          }
          handleBackgroundSelectTeamFileError={
            freeKickDisplayHook.handleBackgroundSelectTeamFileError
          }
          handleBackgroundImageFileError={
            freeKickDisplayHook.handleBackgroundImageFileError
          }
          handleBackgroundNextLevelFileError={
            freeKickDisplayHook.handleBackgroundNextLevelFileError
          }
          handleMissImageFileError={
            freeKickDisplayHook.handleMissImageFileError
          }
          handleOutImageFileError={freeKickDisplayHook.handleOutImageFileError}
          handleGameOverImageFileError={
            freeKickDisplayHook.handleGameOverImageFileError
          }
          handleGoalImageFileError={
            freeKickDisplayHook.handleGoalImageFileError
          }
          handleHighBarImageFileError={
            freeKickDisplayHook.handleHighBarImageFileError
          }
          handleRightBarImageFileError={
            freeKickDisplayHook.handleRightBarImageFileError
          }
          showGuidelines={freeKickDisplayHook.showGuidelines}
          setShowGuidelines={freeKickDisplayHook.setShowGuidelines}
          previousGames={freeKickDisplayHook.previousGames}
          selectedPreviousGame={freeKickDisplayHook.selectedPreviousGame}
          loadingPreviousGames={freeKickDisplayHook.loadingPreviousGames}
          handlePreviousGameSelect={
            freeKickDisplayHook.handlePreviousGameSelect
          }
        />
      );
    }

    if (isWheelOfFortuneFlow) {
      return (
        <WheelOfFortuneDisplayForm
          languages={activityDisplayHook.languages}
          activeTabIndex={activityDisplayHook.activeTabIndex}
          setActiveTabIndex={activityDisplayHook.setActiveTabIndex}
          currentLangData={activityDisplayHook.currentLangData}
          updateField={activityDisplayHook.updateField}
          handleImageUpload={activityDisplayHook.handleImageUpload}
          handleImageDelete={activityDisplayHook.handleImageDelete}
          handleImageFileError={activityDisplayHook.handleImageFileError}
          imageUploadProgress={activityDisplayHook.imageUploadProgress}
          imageValidationError={activityDisplayHook.imageValidationError}
          showGuidelines={activityDisplayHook.showGuidelines}
          setShowGuidelines={activityDisplayHook.setShowGuidelines}
          slices={activityDisplayHook.slices}
          setSlices={activityDisplayHook.setSlices}
          equalProbability={activityDisplayHook.equalProbability}
          setEqualProbability={activityDisplayHook.setEqualProbability}
          equalPoints={activityDisplayHook.equalPoints}
          setEqualPoints={activityDisplayHook.setEqualPoints}
          sliceProbabilities={activityDisplayHook.sliceProbabilities}
          setSliceProbabilities={activityDisplayHook.setSliceProbabilities}
          slicePoints={activityDisplayHook.slicePoints}
          setSlicePoints={activityDisplayHook.setSlicePoints}
          gameSettingsActiveTabIndex={
            activityDisplayHook.gameSettingsActiveTabIndex
          }
          setGameSettingsActiveTabIndex={
            activityDisplayHook.setGameSettingsActiveTabIndex
          }
          gameSettingsCurrentLangData={
            activityDisplayHook.gameSettingsCurrentLangData
          }
          updateGameSettingsField={activityDisplayHook.updateGameSettingsField}
          totalProbability={activityDisplayHook.totalProbability}
        />
      );
    }

    return null;
  };

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {label: '', href: ROUTES.HOME},
            {label: 'Loyalty Campaigns', href: ROUTES.ACTIVITIES},
            {label: 'Add Activity', href: ROUTES.ACTIVITIES}
          ]
        }}
        id='activity_display_breadcrumb'
      />
      <PageHeader title='Add Activity' />
      <ProgressIndicator
        currentStep={2}
        steps={
          isWheelOfFortuneFlow || isFreeKickGameFlow || isBackgammonFlow ? 4 : 3
        }
        titles={
          isWheelOfFortuneFlow || isFreeKickGameFlow || isBackgammonFlow
            ? [
                {title: 'Setup'},
                {title: 'Display'},
                {title: 'Attributes'},
                {title: 'Review'}
              ]
            : [{title: 'Setup'}, {title: 'Display'}, {title: 'Review'}]
        }
        className='mb-6'
        onStepClick={(step) => {
          if (step === 1) navigate(ROUTES.ACTIVITY_ADD);
          if (
            (isWheelOfFortuneFlow || isFreeKickGameFlow || isBackgammonFlow) &&
            step === 3
          ) {
            navigate(ROUTES.ACTIVITY_ATTRIBUTES);
          }
          if (
            ((isWheelOfFortuneFlow || isFreeKickGameFlow || isBackgammonFlow) &&
              step === 4) ||
            (!(
              isWheelOfFortuneFlow ||
              isFreeKickGameFlow ||
              isBackgammonFlow
            ) &&
              step === 3)
          ) {
            navigate(ROUTES.ACTIVITY_REVIEW);
          }
        }}
      />
      {renderDisplayForm()}
      <SaveCancelFooter
        handleCancel={activeHook.handleCancel}
        nextAction={handleNext}
        draftAction={handleDraft}
        FormError={!activeHook.isFormValid}
        nextButtonTitle='Next'
        disableDraft={!isActivitySaveDraftEnabled(status)}
      />
      {activeHook.isCancel &&
        getModal({
          title: 'Are you sure you want to cancel?',
          text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
          showPrimaryButton: true,
          primaryButtonText: 'Yes',
          onClickPrimaryButton: activeHook.onConfirmCancel,
          showSecondaryButton: true,
          secondaryButtonText: 'No',
          onClickSecondaryButton: activeHook.onDismissCancel,
          showModal: activeHook.isCancel,
          onCloseModal: activeHook.handleCancel,
          hideCloseButton: false
        })}
    </>
  );
};

export default ActivityDisplay;
